import { getApiRoute } from '@/lib/config/urls';
import { reportClientError } from './error-reporter';
import type { ApiResponse } from './types';
import { ApiErrorCode } from '@/lib/shared-types';
import { isRecord, safeJsonParseRecord } from '@/lib/utils/safe-json';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/stores/auth';

// Type guard for ApiResponse
function isApiResponse<T = unknown>(value: unknown): value is ApiResponse<T> {
  return isRecord(value) && typeof value.success === 'boolean';
}

// Type guard for error objects
function isErrorObject(value: unknown): value is Record<string, unknown> {
  return isRecord(value);
}

export type RequestConfig = RequestInit & {
  url: string;
  skipAuth?: boolean;
  timeout?: number;
  _retry?: boolean;
};

type RequestInterceptor = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
type ResponseInterceptor = (response: Response, config: RequestConfig) => Response | Promise<Response>;
type ErrorInterceptor = (error: Error, config: RequestConfig) => Error | Promise<Error>;

class ApiClient {
  private static instance: ApiClient;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];
  private pendingRequests = new Map<string, Promise<ApiResponse>>();
  private csrfToken: string | null = null;
  private csrfFetchPromise: Promise<string | null> | null = null;
  private backendUnavailableUntil = 0;
  private backendUnavailableNoticeLogged = false;

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  constructor() {
    this.initializeInterceptors();
  }

  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  addErrorInterceptor(interceptor: ErrorInterceptor): void {
    this.errorInterceptors.push(interceptor);
  }

  isBackendUnavailable(): boolean {
    return Date.now() < this.backendUnavailableUntil;
  }

  clearBackendUnavailable(): void {
    this.backendUnavailableUntil = 0;
    this.backendUnavailableNoticeLogged = false;
  }

  private markBackendUnavailable(ttlMs = 30_000): boolean {
    const now = Date.now();
    // Allow logging if we haven't logged yet, OR if the previous lockout period has expired.
    const shouldLog = !this.backendUnavailableNoticeLogged || now > this.backendUnavailableUntil;

    this.backendUnavailableUntil = now + ttlMs;
    this.backendUnavailableNoticeLogged = true;
    return shouldLog;
  }

  async fetchCSRFToken(): Promise<string | null> {
    // Deduplicate concurrent CSRF token fetches
    if (this.csrfFetchPromise) return this.csrfFetchPromise;

    this.csrfFetchPromise = (async () => {
      try {
        const url = getApiRoute('/api/csrf-token');
        const response = await fetch(url, { method: 'GET', credentials: 'include' });
        if (!response.ok) return null;
        const data = await response.json().catch(() => null);
        const responseData = isRecord(data) && isRecord(data.data) ? data.data : null;
        this.csrfToken = responseData && typeof responseData.csrfToken === 'string' ? responseData.csrfToken : null;
        return this.csrfToken;
      } catch {
        return null;
      } finally {
        this.csrfFetchPromise = null;
      }
    })();

    return this.csrfFetchPromise;
  }

  private initializeInterceptors(): void {
    // Request interceptor: Add auth token + CSRF token
    this.addRequestInterceptor(async (config) => {
      // Convert headers to a plain object to ensure consistent behavior
      // Headers can be Headers object, string[][], or Record<string, string>
      let headers: Record<string, string> = {};
      if (config.headers) {
        if (config.headers instanceof Headers) {
          // Convert Headers object to plain object
          config.headers.forEach((value, key) => {
            headers[key] = value;
          });
        } else if (Array.isArray(config.headers)) {
          // Convert string[][] to plain object
          for (const [key, value] of config.headers) {
            headers[key] = value;
          }
        } else {
          // Already a plain object
          headers = { ...config.headers };
        }
      }

      if (!config.skipAuth) {
        // Read the Supabase access token directly from localStorage to avoid the
        // navigator.locks deadlock in Supabase JS v2.90+ (supabase.auth.getSession()
        // acquires a Web Lock that can hang indefinitely in certain browser contexts).
        // Reading storage directly is safe here — we only need the current token value;
        // the Supabase auto-refresh listener updates storage when tokens rotate.
        let token: string | null = null;
        try {
          const sbKey = Object.keys(localStorage).find(
            k => k.startsWith('sb-') && k.endsWith('auth-token')
          );
          if (sbKey) {
            const raw = localStorage.getItem(sbKey);
            if (raw) {
              const parsed = safeJsonParseRecord(raw);
              token = parsed && typeof parsed.access_token === 'string' ? parsed.access_token : null;
            }
          }
        } catch {
          token = null;
        }

        if (token && typeof token === 'string' && token.trim().length > 0) {
          headers['Authorization'] = `Bearer ${token}`;
        } else {
          delete headers['Authorization'];
        }
      }

      // Default headers - but don't set for FormData (browser needs to set Content-Type with boundary)
      if (config.body && !(config.body instanceof FormData) && !('Content-Type' in headers)) {
        headers['Content-Type'] = 'application/json';
      }

      // Add CSRF token for state-changing requests
      const method = (config.method || 'GET').toUpperCase();
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        const token = this.csrfToken ?? await this.fetchCSRFToken();
        if (token) {
          headers['X-CSRF-Token'] = token;
        }
      }

      config.headers = headers;
      return config;
    });

    // Response interceptor: Handle 401s (optional, Supabase client usually handles refresh before request)
    this.addResponseInterceptor(async (response, config) => {
      // We could add retry logic here if needed, but Supabase getSession() above manages tokens well.
      return response;
    });
  }

  private readMessageField(value: unknown): string | undefined {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'message' in value && typeof (value as { message?: unknown }).message === 'string') {
      return (value as { message: string }).message;
    }
    return undefined;
  }

  async request<T = unknown>(
    endpoint: string,
    options: RequestInit & { skipAuth?: boolean; timeout?: number; skipDedupe?: boolean } = {}
  ): Promise<ApiResponse<T>> {
    const url = endpoint.startsWith('http') ? endpoint : getApiRoute(endpoint);
    const config: RequestConfig = { url, ...options };
    const timeoutMs = options.timeout ?? 15_000;

    try {
      if (this.isBackendUnavailable()) {
        return {
          success: false,
          error: {
            code: ApiErrorCode.SERVICE_UNAVAILABLE,
            message: 'Service temporarily unavailable. Please try again later.'
          }
        };
      }

      // Apply request interceptors
      let processedConfig = config;
      for (const interceptor of this.requestInterceptors) {
        processedConfig = await interceptor(processedConfig);
      }

      // Include credentials for CORS requests when backend sets Access-Control-Allow-Credentials: true
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

      let response: Response;
      try {
        response = await fetch(processedConfig.url, {
          ...processedConfig,
          credentials: 'include',  // Required for CORS with Access-Control-Allow-Credentials: true
          signal: processedConfig.signal ?? controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }

      this.clearBackendUnavailable();

      // Apply response interceptors
      let processedResponse = response;
      for (const interceptor of this.responseInterceptors) {
        processedResponse = await interceptor(processedResponse, processedConfig);
      }

      if (!processedResponse.ok) {
        // Handle 401 Unauthorized - clear auth state and redirect to login
        if (processedResponse.status === 401) {
          const authStore = useAuthStore.getState();
          if (authStore.isAuthenticated) {
            console.warn('[API Client] Received 401, clearing auth state');
            authStore.logout();
            toast.error('Session expired', {
              description: 'Please sign in again to continue.',
              duration: 5000
            });
          }
        }

        // If CSRF token expired/invalid, clear cache so next request re-fetches
        if (processedResponse.status === 403) {
          const cloned = processedResponse.clone();
          const errBody = await cloned.json().catch(() => null);
          const errorObj = isRecord(errBody) && isRecord(errBody.error) ? errBody.error : null;
          if (errorObj?.code === 'CSRF_INVALID') {
            this.csrfToken = null;
          }
        }

        const errorData = (await processedResponse.json().catch(() => ({}))) as unknown;

        // Type guard to ensure errorData is an object
        if (!isErrorObject(errorData)) {
          return {
            success: false,
            error: {
              code: ApiErrorCode.INTERNAL_ERROR,
              message: `Request failed (${processedResponse.status})`
            }
          };
        }

        let errorMessage = 'Request failed';
        if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        } else if (typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        } else if (isErrorObject(errorData.error)) {
          errorMessage = this.readMessageField(errorData.error) || JSON.stringify(errorData.error);
        } else if (isErrorObject(errorData.message)) {
          errorMessage = this.readMessageField(errorData.message) || JSON.stringify(errorData.message);
        } else {
          errorMessage = `Request failed (${processedResponse.status})`;
        }

        // Extract error code from response if available
        let errorCode = ApiErrorCode.INTERNAL_ERROR;
        if (isErrorObject(errorData) && isErrorObject(errorData.error)) {
          const code = (errorData.error as { code?: string }).code;
          if (code) {
            errorCode = code as ApiErrorCode;
          }
        }

        return {
          success: false,
          error: {
            code: errorCode,
            message: errorMessage
          }
        };
      }

      const data = (await processedResponse.json()) as unknown;

      // Type guard to ensure data is an object
      if (!isErrorObject(data)) {
        return { success: true, data: data as T };
      }

      const payload = 'data' in data
        ? (data.data as T) ?? (data as T)
        : (data as T);
      return { success: true, data: payload }; // Handle wrapped or direct data

    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return {
          success: false,
          error: {
            code: ApiErrorCode.SERVICE_UNAVAILABLE,
            message: 'Request timed out. Please try again later.'
          }
        };
      }

      // Check if this is a connection refused error (backend not running)
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        const shouldLog = this.markBackendUnavailable();
        if (shouldLog) {
          if (import.meta.env.DEV) {
            console.error(
              '🚨 Backend not available. Make sure to run: pnpm run dev:backend\n' +
              'Or start both with: pnpm run dev'
            );
          }
          toast.error("No internet connection or backend is unavailable.", {
            description: "Please check your network and try again.",
            duration: 5000,
            id: "backend-unavailable" // Prevent duplicates
          });
        }

        return {
          success: false,
          error: {
            code: ApiErrorCode.SERVICE_UNAVAILABLE,
            message: 'Service temporarily unavailable. Please try again later.'
          }
        };
      }

      console.error('API Request failed:', error);
      // Report genuine request failures (not aborts, not offline fetch
      // failures) so we learn about backend errors from real users.
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        reportClientError({
          message:
            error instanceof Error
              ? `API request failed: ${error.message}`
              : 'API request failed',
          errorName: error instanceof Error ? error.name : 'ApiRequestError',
          stack: error instanceof Error ? error.stack : undefined,
          screen: 'api',
          code: 'API_REQUEST_FAILED',
        });
      }
      const errorMessage = (error instanceof TypeError && error.message.includes('Failed to fetch'))
        ? 'Service temporarily unavailable. Please try again later.'
        : error instanceof Error ? error.message : 'Network request failed';
      return {
        success: false,
        error: {
          code: ApiErrorCode.SERVICE_UNAVAILABLE,
          message: errorMessage
        }
      };
    }
  }

  async get<T = unknown>(
    endpoint: string,
    options: Omit<RequestInit, 'method'> & { skipAuth?: boolean; timeout?: number } = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T = unknown>(
    endpoint: string,
    data?: unknown,
    options: Omit<RequestInit, 'method' | 'body'> & { skipAuth?: boolean; timeout?: number } = {}
  ): Promise<ApiResponse<T>> {
    let body: BodyInit | undefined;
    if (data instanceof FormData) {
      body = data;
    } else if (data) {
      body = JSON.stringify(data);
    }
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body,
    });
  }

  async put<T = unknown>(
    endpoint: string,
    data?: unknown,
    options: Omit<RequestInit, 'method' | 'body'> & { skipAuth?: boolean; timeout?: number } = {}
  ): Promise<ApiResponse<T>> {
    let body: BodyInit | undefined;
    if (data instanceof FormData) {
      body = data;
    } else if (data) {
      body = JSON.stringify(data);
    }
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body,
    });
  }

  async delete<T = unknown>(
    endpoint: string,
    options: Omit<RequestInit, 'method'> & { skipAuth?: boolean; timeout?: number } = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const apiClient = ApiClient.getInstance();

export const api = {
  get: apiClient.get.bind(apiClient),
  post: apiClient.post.bind(apiClient),
  put: apiClient.put.bind(apiClient),
  delete: apiClient.delete.bind(apiClient),
  request: apiClient.request.bind(apiClient),
  prefetchCSRFToken: apiClient.fetchCSRFToken.bind(apiClient),
  isBackendUnavailable: apiClient.isBackendUnavailable.bind(apiClient),
  clearBackendUnavailable: apiClient.clearBackendUnavailable.bind(apiClient),
};
